import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, EmptyState, Toggle } from '@playerbase/ui';
import type { Playtest } from '@playerbase/types';

import { fetchPlaytestsForGame } from '../../games/playtests-data.js';
import { fetchActiveSessionCountsByPlaytest } from '../../games/playtest-sessions-data.js';
import { useShell } from '../../shell/use-shell.js';
import { usePlaytestsRealtime } from '../../games/use-playtests-realtime.js';

interface ListRow {
  playtest: Playtest;
  activeSessions: number;
  lastActivityAt: string | null;
}

interface PlaytestsListPaneProps {
  selectedPlaytestId: string | null;
}

// Narrow left column of the three-column playtest layout. Owns the
// playtests list + archived toggle; navigates by Link so the URL drives
// the middle/right panes.
export function PlaytestsListPane({ selectedPlaytestId }: PlaytestsListPaneProps): JSX.Element {
  const { activeGame, playtestSessionsVersion } = useShell();
  const { playtestsVersion } = usePlaytestsRealtime();
  const navigate = useNavigate();
  const params = useParams<{ studioShortId: string; gameShortId: string }>();

  const [rows, setRows] = useState<ListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [playtests, counts] = await Promise.all([
      fetchPlaytestsForGame(activeGame.id),
      fetchActiveSessionCountsByPlaytest(activeGame.id),
    ]);
    setRows(
      playtests.map((p) => ({
        playtest: p,
        activeSessions: counts[p.id]?.active ?? 0,
        lastActivityAt: counts[p.id]?.lastActivityAt ?? null,
      })),
    );
    setLoading(false);
  }, [activeGame]);

  useEffect(() => {
    void reload();
  }, [reload, playtestsVersion, playtestSessionsVersion]);

  const visibleRows = useMemo(
    () =>
      showArchived
        ? rows
        : rows.filter((row) => row.playtest.archivedAt === null),
    [rows, showArchived],
  );
  const archivedCount = rows.filter((r) => r.playtest.archivedAt !== null).length;

  if (activeGame === null) {
    return <div className="px-4 py-6 text-sm text-text-muted">No game selected.</div>;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-surface md:border-r md:border-border">
      <header className="flex flex-col gap-0.5 border-b border-border px-4 py-3">
        <h2 className="font-display text-lg font-semibold text-text">Playtests</h2>
        <p className="text-xs text-text-muted">Pick one to see its sessions.</p>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto py-3">
        {loading ? (
          <div className="px-4 text-sm text-text-muted">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="px-4">
            <EmptyState
              title="No playtests yet"
              description="Configure a playtest with the events you want to ask testers about."
              cta={
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate(
                      `/s/${params.studioShortId}/g/${params.gameShortId}/playtest-settings/flows`,
                    )
                  }
                >
                  Configure first playtest
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {archivedCount > 0 ? (
              <div className="flex items-center gap-2 px-4 text-xs text-text-muted">
                <span id="show-archived-playtests-label">
                  Show archived ({archivedCount})
                </span>
                <Toggle
                  aria-label="Show archived playtests"
                  aria-labelledby="show-archived-playtests-label"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
              </div>
            ) : null}
            <ul className="flex flex-col divide-y divide-border border-y border-border">
              {visibleRows.map((row) => {
                const isSelected = row.playtest.id === selectedPlaytestId;
                return (
                  <li key={row.playtest.id}>
                    <Link
                      to={`/s/${params.studioShortId}/g/${params.gameShortId}/playtests/${row.playtest.id}`}
                      aria-current={isSelected ? 'page' : undefined}
                      className={
                        isSelected
                          ? 'flex flex-col gap-1 bg-accent/10 px-4 py-3 transition'
                          : 'flex flex-col gap-1 px-4 py-3 transition hover:bg-surface-2'
                      }
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                          {row.playtest.name}
                        </span>
                        <StatusBadges
                          status={row.playtest.status}
                          archivedAt={row.playtest.archivedAt}
                        />
                      </div>
                      <span className="text-xs text-text-muted">
                        {row.activeSessions === 1
                          ? '1 active'
                          : `${row.activeSessions} active`}
                        {row.lastActivityAt !== null
                          ? ` · ${formatRelative(row.lastActivityAt)}`
                          : ''}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

const STATUS_TONE: Record<Playtest['status'], 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  active: 'success',
  disabled: 'warning',
};

const STATUS_LABEL: Record<Playtest['status'], string> = {
  draft: 'Draft',
  active: 'Active',
  disabled: 'Disabled',
};

function StatusBadges({
  status,
  archivedAt,
}: {
  status: Playtest['status'];
  archivedAt: Playtest['archivedAt'];
}): JSX.Element {
  return (
    <span className="flex items-center gap-1.5">
      <Badge tone={STATUS_TONE[status]} aria-label={`Status: ${STATUS_LABEL[status]}`}>
        {STATUS_LABEL[status]}
      </Badge>
      {archivedAt !== null ? <Badge>Archived</Badge> : null}
    </span>
  );
}

function formatRelative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return new Date(then).toLocaleDateString();
}
